import React, { useState } from "react";
import { useDrop } from "react-dnd";

import MagnetboardSection from "./MagnetboardSection";
import Loader from "../../common/Loader";


const MagnetboardUnplacedDropZone = React.memo(({
  schoolInfo,
  scheduleInfo,
  scheduleStructureData,
  scheduleType,
  currentView,
  currentViewType,
  currentDay,
  currentScheduleVersionID,
  teachers,
  courses,
  classrooms,
  sections,
  setSections,
  subsections,
  labels,            
  setConflictsLoading,
  setMatchPercent,
  setMatchPercentLoading,
  toggleEditSection,
  duplicateSectionFromMagnetboard,
  deleteSectionFromMagnetboard,
  setDuplicateLoadingTarget,
  deleteLoadingTargets,
  setDeleteLoadingTarget,
  updateManualPlacement,
  sectionIsAllowedForCurrentUser = () => true,
  toggleLockSection,
  sectionConflictSummaryBySectionId,
  magnetboardOptions = {},
  dataID,
  unassignedSections,
  handleSelectData,
}) => {
  const [isDropLoading, setIsDropLoading] = useState(false);
  const canDragSections = magnetboardOptions.allow_section_dragging !== false;
  const canEditSectionStructure = magnetboardOptions.allow_section_structure_editing !== false;
  const canEditSectionRoster = magnetboardOptions.allow_section_roster_editing !== false;

  /////////////////////////////
  ///// USE DROP FUNCTION /////
  /////////////////////////////
  const [{ isActive, isOver }, dropRef] = useDrop({
    accept: ["section"],
    canDrop: (item) => {
      const section_info = sections.find(section => `${section.section_id}` === `${item.sectionID}`);
      if (!sectionIsAllowedForCurrentUser(section_info)) return false;
      if (currentView === "classroom") return true;

      return `${item.draggedDataID || ""}` === `${dataID || ""}`;
    },
    drop: async (item) => {
      const section_info = sections.find(section => `${section.section_id}` === `${item.sectionID}`);
      if (!sectionIsAllowedForCurrentUser(section_info)) return;
      if (
        currentView !== "classroom" &&
        `${item.draggedDataID || ""}` !== `${dataID || ""}`
      ) {
        return;
      }

      setIsDropLoading(true);
      try {
        await updateManualPlacement({
          updateType: "undo",
          sectionID: item.sectionID,
          droppedCoursePeriodID: null,
          draggedCoursePeriodID: item.draggedCoursePeriodID || null,
        });
      } catch (error) {
        console.error("UNPLACED DROP PERIOD ERROR:", error);
      } finally {
        setIsDropLoading(false);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isActive: monitor.isOver(),
    }),
  });

  /////////////////////
  ///// USE EFFECT /////
  /////////////////////
  return (
    <div className="mb-unassigned-sections-container">
      <div className="dashboard-grid">
        <h5 style={{ margin: "0px 0px 5px 0px" }}>Unplaced Sections</h5>
        <div
          className="align-right small-text dark-gray-link"
          onClick={() => handleSelectData(null)}
        >
          Close
        </div>
      </div>
      <div
        className={`mb-unassigned-sections-dropzone ${
          isActive && "mb-unassigned-sections-hover-over"
        }`}
        ref={dropRef}
        style={{
          position: 'relative',
        }}
      >
        {/* Dashed Line Drop Indicator or Loader */}
        {(isOver || isDropLoading) && (
          <div className="mb-schedule-inner-box-dashed-line">
            {isDropLoading ? (
              <Loader size="48px" />
            ) : null }
          </div>
        )}
        {unassignedSections.map((section_info) => {
          const section_can_edit = sectionIsAllowedForCurrentUser(section_info);
          const can_open_section = section_can_edit && (
            currentView === "student" ? canEditSectionRoster : (canEditSectionStructure || canEditSectionRoster)
          );
          return (
            <MagnetboardSection
              key={`unplaced-${section_info.section_id}`}
              schoolInfo={schoolInfo}
              scheduleInfo={scheduleInfo}
              scheduleStructureData={scheduleStructureData}
              scheduleType={scheduleType}
              currentView={currentView}
              currentViewType={currentViewType}
              currentDay={currentDay}
              currentScheduleVersionID={currentScheduleVersionID}
              teachers={teachers}
              courses={courses}
              classrooms={classrooms}
              sections={sections}
              setSections={setSections}
              subsections={subsections}
              labels={labels}
              setConflictsLoading={setConflictsLoading}
              setMatchPercent={setMatchPercent}
              setMatchPercentLoading={setMatchPercentLoading}
              labsOnlyFiltered={false}
              nonLabsOnlyFiltered={false}
              singletonsFiltered={false}
              doubletonsFiltered={false}
              noClassroomFiltered={false}
              hasClassroomFiltered={false}
              toggleEditSection={toggleEditSection}
              duplicateSectionFromMagnetboard={duplicateSectionFromMagnetboard}
              deleteSectionFromMagnetboard={deleteSectionFromMagnetboard}
              setDuplicateLoadingTarget={setDuplicateLoadingTarget}
              deleteLoadingTargets={deleteLoadingTargets}
              setDeleteLoadingTarget={setDeleteLoadingTarget}
              dataID={dataID}
              coursePeriodID={null}
              sectionID={section_info.section_id}
              sectionType="manual-section"
              toggleLockSection={toggleLockSection}
              isDraggable={canDragSections && section_can_edit}
              isEditable={can_open_section}
              isLockable={currentView !== "student" && can_open_section}
              isQuickViewEnabled={currentView !== "student" && canEditSectionStructure}
              sectionConflictSummaryBySectionId={sectionConflictSummaryBySectionId}
              magnetboardOptions={magnetboardOptions}
            />
          );
        })}
      </div>
    </div>
  );
});

export default MagnetboardUnplacedDropZone;
